// Currency utilities for Payday Control
// Display-only currency formatting (no conversion)

export const CURRENCIES = {
  GBP: { code: 'GBP', symbol: '£', name: 'British Pound', locale: 'en-GB' },
  USD: { code: 'USD', symbol: '$', name: 'US Dollar', locale: 'en-US' },
  EUR: { code: 'EUR', symbol: '€', name: 'Euro', locale: 'de-DE' },
  CAD: { code: 'CAD', symbol: '$', name: 'Canadian Dollar', locale: 'en-CA' },
  AUD: { code: 'AUD', symbol: '$', name: 'Australian Dollar', locale: 'en-AU' }
};

export const DEFAULT_CURRENCY = 'GBP';

/**
 * Format a number as currency
 * @param {number} amount - The amount to format
 * @param {string} currencyCode - Currency code (GBP, USD, EUR, CAD, AUD)
 * @param {boolean} showSymbol - Whether to show currency symbol
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, currencyCode = DEFAULT_CURRENCY, showSymbol = true) => {
  const currency = CURRENCIES[currencyCode] || CURRENCIES[DEFAULT_CURRENCY];
  
  if (amount === null || amount === undefined || isNaN(amount)) {
    return showSymbol ? `${currency.symbol}0.00` : '0.00';
  }
  
  const absAmount = Math.abs(amount);
  const formatted = absAmount.toFixed(2);
  
  if (showSymbol) {
    // For CAD and AUD, add currency code to distinguish from USD
    if (currencyCode === 'CAD') {
      return amount < 0 ? `-CA$${formatted}` : `CA$${formatted}`;
    }
    if (currencyCode === 'AUD') {
      return amount < 0 ? `-A$${formatted}` : `A$${formatted}`;
    }
    return amount < 0 ? `-${currency.symbol}${formatted}` : `${currency.symbol}${formatted}`;
  }
  
  return amount < 0 ? `-${formatted}` : formatted;
};

/**
 * Get currency symbol
 * @param {string} currencyCode - Currency code
 * @returns {string} Currency symbol
 */
export const getCurrencySymbol = (currencyCode = DEFAULT_CURRENCY) => {
  const currency = CURRENCIES[currencyCode] || CURRENCIES[DEFAULT_CURRENCY];
  if (currencyCode === 'CAD') return 'CA$';
  if (currencyCode === 'AUD') return 'A$';
  return currency.symbol;
};

/**
 * Get currency name
 * @param {string} currencyCode - Currency code
 * @returns {string} Currency name
 */
export const getCurrencyName = (currencyCode = DEFAULT_CURRENCY) => {
  const currency = CURRENCIES[currencyCode] || CURRENCIES[DEFAULT_CURRENCY];
  return currency.name;
};

export default { CURRENCIES, DEFAULT_CURRENCY, formatCurrency, getCurrencySymbol, getCurrencyName };
